// src/pages/Login.jsx
import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase, readCachedSession } from "../supabaseClient";
import { readOfflineIdentity, isSignedOut } from "../lib/offlineIdentity";
import { useAuth } from "../contexts/AuthContext";
import theme from "../theme";
import LoginChart from "../components/LoginChart";

/**
 * Dev login using RPC verify_test_user(in_email, in_password)
 * Stores roleS/email/id in localStorage and routes to /hub.
 */
export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  // "error" paints red; anything else green. Severity used to be guessed from
  // whether the text contained "Invalid", so "Login failed" and rate-limit
  // errors showed green (Codex review 2026-09-04).
  const [messageTone, setMessageTone] = useState("info");
  function say(text, tone = "info") { setMessageTone(tone); setMessage(text); }
  const [noInternet, setNoInternet] = useState(false);
  // This device holds a previous sign-in's work (saved session or token-less
  // identity) — the "Continue offline" door only makes sense then.
  const [hasDeviceWork, setHasDeviceWork] = useState(false);
  // set by the Continue-offline tap; navigation waits for the session to
  // actually LAND in context (navigating in the same breath raced the state
  // update and could bounce straight back here)
  const [continuedOffline, setContinuedOffline] = useState(false);
  // idle | trying | failed — landing here with saved work and no Sign Out tap
  // auto-resumes; the form only appears when resuming isn't possible.
  const [autoResume, setAutoResume] = useState("idle");
  const [diag, setDiag] = useState("");
  const { continueOffline, session, signedOutExplicitly } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // "Sign in to sync" (the needsReauth banner) links here as /login?reauth=1 —
  // the ONE case where someone with a live session should see this form.
  const cameForReauth = searchParams.get("reauth") === "1";

  // THE LOOP KILLER (Jill's iPad, 2026-07-11, second report): the offline
  // boot restores the session in the background moments after a redirect
  // parks someone here — but this page had no "you're already signed in"
  // rule, so it just sat showing an unusable form. If a session exists and
  // they didn't come to re-sign-in deliberately, go straight to their work.
  useEffect(() => {
    if (session && !cameForReauth && !signedOutExplicitly) {
      navigate("/", { replace: true });
    }
  }, [session, cameForReauth, signedOutExplicitly, navigate]);

  // Which storage layers does this device actually hold? Shown as a short
  // support code when a resume fails, so "it looped again" reports become
  // diagnosable (tok/bak/id = login token, backup, identity in localStorage;
  // idb = the durable vault answered / was empty / hung / errored).
  async function buildDiag() {
    const parts = [`v=${typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "dev"}`];
    try {
      const ref = new URL(import.meta.env.VITE_SUPABASE_URL).hostname.split(".")[0];
      parts.push(`tok=${localStorage.getItem(`sb-${ref}-auth-token`) ? "y" : "n"}`);
      parts.push(`bak=${localStorage.getItem("seapa_session_backup") ? "y" : "n"}`);
      parts.push(`id=${localStorage.getItem("seapa_offline_identity") ? "y" : "n"}`);
    } catch { parts.push("ls=err"); }
    const idb = await new Promise((resolve) => {
      let done = false;
      const finish = (v) => { if (!done) { done = true; resolve(v); } };
      setTimeout(() => finish("hang"), 1500);
      try {
        const req = indexedDB.open("seapa-durable", 1);
        req.onupgradeneeded = () => { try { req.result.createObjectStore("kv"); } catch { /* exists */ } };
        req.onerror = () => finish("err");
        req.onsuccess = () => {
          try {
            const g = req.result.transaction("kv", "readonly").objectStore("kv").get("seapa_offline_identity");
            g.onsuccess = () => { finish(g.result ? "y" : "empty"); req.result.close(); };
            g.onerror = () => { finish("err"); req.result.close(); };
          } catch { finish("err"); }
        };
      } catch { finish("err"); }
    });
    parts.push(`idb=${idb}`);
    setDiag(parts.join(" "));
  }

  // Jill's rule (2026-07-11): NOBODY lands on a login form while this device
  // holds their work, unless they truly tapped Sign Out. Whatever routed them
  // here, resume automatically; the offline banner tells them the rest.
  useEffect(() => {
    if (session || signedOutExplicitly || !hasDeviceWork) return;
    if (autoResume !== "idle") return;
    setAutoResume("trying");
    (async () => {
      // The signed-out flag may exist only in the IndexedDB mirror (iOS
      // dropped localStorage); continueOffline() is the explicit door and
      // would walk past it, so check first (Codex review of #506).
      if (await isSignedOut()) { setAutoResume("signed-out"); return; }
      const ok = await continueOffline();
      if (ok) setContinuedOffline(true);
      else { setAutoResume("failed"); buildDiag(); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, signedOutExplicitly, hasDeviceWork, autoResume]);

  useEffect(() => {
    // Only auto-leave after an explicit Continue-offline tap — someone with a
    // running offline session who came here via "Sign in to sync" must be
    // able to stay and type their password.
    if (continuedOffline && session) navigate("/", { replace: true });
  }, [continuedOffline, session, navigate]);

  useEffect(() => {
    let stopped = false;
    (async () => {
      const cached = readCachedSession();
      const identity = cached ? null : await readOfflineIdentity();
      if (!stopped) setHasDeviceWork(Boolean(cached?.user || identity?.userId));
    })();
    return () => { stopped = true; };
  }, []);

  async function handleContinueOffline() {
    say("Opening this device's saved work...");
    const ok = await continueOffline();
    if (ok) {
      setContinuedOffline(true); // the effect above navigates once the session lands
    } else {
      say("Couldn't open this device's saved work. Signing in needs an internet connection.", "error");
      buildDiag();
    }
  }

  // Dead-wifi detector (Jill's iPad on PPU wifi, 2026-07-11): "connected"
  // wifi with no internet behind it let her type a password into a void.
  // Probe a tiny auth endpoint; if it can't be reached, say so up front.
  // Keeps re-checking so the notice clears on its own when internet is back.
  useEffect(() => {
    let stopped = false;
    const probe = async () => {
      try {
        const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/auth/v1/health`, {
          method: "GET",
          cache: "no-store",
          headers: { apikey: import.meta.env.VITE_SUPABASE_ANON_KEY },
          ...(typeof AbortSignal !== "undefined" && AbortSignal.timeout ? { signal: AbortSignal.timeout(4000) } : {}),
        });
        if (!stopped) setNoInternet(!res.ok);
      } catch {
        if (!stopped) setNoInternet(true);
      }
    };
    probe();
    const iv = setInterval(probe, 15000);
    return () => { stopped = true; clearInterval(iv); };
  }, []);

  async function handleLogin(e) {
    console.log("🟢 LOGIN: handleLogin started", {
  time: new Date().toISOString(),
  path: window.location.pathname,
});
    e.preventDefault();
    setLoading(true);
    say("");

    try {
      // 1) Try REAL Supabase auth first (magic link / password)
      const { data: authData, error: authError } =
        await supabase.auth.signInWithPassword({
          email,
          password,
        });

      if (!authError) {
        console.log("🟢 LOGIN: Supabase login SUCCESS", {
          time: new Date().toISOString(),
          userId: authData?.user?.id,
        });

        // Clear spinner FIRST, then navigate
        setLoading(false);
        navigate("/", { replace: true });
        return;
      }

      // Auth rejected (wrong password, rate limit, etc.) — show it and
      // reset the button instead of hanging on "Verifying..."
      say(authError.message || "Login failed", "error");
      setLoading(false);
    } catch (err) {
      console.error("❌ Login failed:", err);
      // "Load failed" / "Failed to fetch" = no network, not bad credentials
      const offline = navigator.onLine === false || /load failed|failed to fetch|network/i.test(err.message || "");
      say(offline
        ? "You appear to be offline. Signing in needs an internet connection. If you were already signed in, reconnect and the portal will pick your session back up."
        : (err.message || "Login failed"), "error");
      setLoading(false);
    }
  }

  return (
    <div style={{ position: "relative", minHeight: "100vh", background: "var(--page)", overflow: "hidden", padding: "0 16px" }}>
      <LoginChart />
      <div
        style={{
          position: "relative",
          zIndex: 1,
          maxWidth: 400,
          margin: "0 auto",
          padding: 24,
          top: "10vh",
          borderRadius: 12,
          boxShadow: "var(--shadow-raised)",
          border: "1px solid var(--border)",
          fontFamily: "inherit",
          background: "var(--surface)",
        }}
      >
      {autoResume === "trying" && !session ? (
        <p style={{ textAlign: "center", color: "var(--ink)", margin: "24px 0" }}>
          Opening your saved work...
        </p>
      ) : (
      <>
      {/* branding moved here from the retired page-wide watermark */}
      {/* white chip behind the crest so the logo sits deliberately in dark mode too */}
      <img src="/assets/SeapaLogo.png" alt="SEAPA" style={{ display: "block", width: 88, height: 88, margin: "0 auto 14px", objectFit: "contain", background: "#ffffff", borderRadius: 12, padding: 6, boxSizing: "border-box" }} />
      {/* The full name, set like a chart's title block (design review item 24) */}
      <h1 style={{ textAlign: "center", margin: 0, color: "var(--primary)", fontSize: "1.02rem", fontWeight: 800, letterSpacing: "0.18em", lineHeight: 1.5, textTransform: "uppercase" }}>
        Southeast Alaska<br />Pilots&#39; Association
      </h1>
      <div aria-hidden="true" style={{ width: 44, height: 3, borderRadius: 2, background: "var(--gold)", margin: "10px auto 8px" }} />
      <p style={{ textAlign: "center", margin: "0 0 18px", color: "var(--text-muted)", fontSize: "0.85rem", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase" }}>
        Portal login
      </p>

      {noInternet && (
        <div style={{
          background: "var(--warn-bg)",
          color: "var(--warn-fg)",
          border: "1px solid var(--warn-border)",
          borderRadius: 8,
          padding: "10px 14px",
          marginBottom: 14,
          fontSize: "0.9rem",
          lineHeight: 1.5,
        }}>
          This wifi doesn't appear to have internet (common on ship and PPU
          wifi). Signing in needs a working connection. This notice will clear
          on its own once internet is back.
          {hasDeviceWork && (
            <button
              type="button"
              onClick={handleContinueOffline}
              style={{
                ...button,
                marginTop: 10,
                background: theme.colors.primary,
                color: "white",
              }}
            >
              Continue offline with this device's saved work
            </button>
          )}
        </div>
      )}

      <form onSubmit={handleLogin}>
        <label>Email</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={input}
        />

        <label>Password</label>
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={input}
        />

        <button
          type="submit"
          disabled={loading}
          style={{
            ...button,
            background: theme.colors.primary,
            color: "white",
            marginTop: 12,
          }}
        >
          {loading ? "Verifying..." : "Login"}
        </button>

        <button
          type="button"
          onClick={async () => {
            // An empty box used to come back as Supabase's "One of email or
            // phone must be set", which people read as "nothing happened"
            // (John Herring 10/30, Katie Montgomery 11/4).
            const linkEmail = email.trim();
            if (!linkEmail) {
              say("Type your email address in the Email box first, then tap Request link.", "error");
              return;
            }
            setLoading(true);
            say("");
            const { error } = await supabase.auth.signInWithOtp({
              email: linkEmail,
              options: {
                emailRedirectTo: `${window.location.origin}/set-password`,
                // never create a new account from the login page — only
                // office-provisioned emails may sign in
                shouldCreateUser: false,
              },
            });
            if (error) {
              say(/signup|not allowed|not found/i.test(error.message)
                ? "That email isn't registered with the portal. Check the spelling (dots matter), or contact the office to get set up."
                : error.message, "error");
            } else {
              say("Sent. Check your email, including the junk folder. Tap the link once.");
            }
            setLoading(false);
          }}
          style={{
            ...button,
            marginTop: 8,
            background: "var(--surface-muted)",
            color: "var(--ink)",
          }}
        >
          Request link to create or reset password.
        </button>
      </form>

      {/* Quiet way out when signing in is the thing that is broken: Support
          is a public route and needs no login (Jill 2026-10-20). */}
      <p style={{ marginTop: 14, textAlign: "center", fontSize: "0.85rem" }}>
        <Link to="/support" style={{ color: "var(--text-muted)" }}>Portal not working? Portal Support</Link>
      </p>

      {message && (
        <p
          role={messageTone === "error" ? "alert" : "status"}
          style={{
            color: messageTone === "error" ? "var(--danger-fg)" : "var(--success-fg)",
            marginTop: 12,
            textAlign: "center",
          }}
        >
          {message}
        </p>
      )}

      {(autoResume === "failed" || diag) && (
        <p style={{ color: "var(--faint)", fontSize: "0.75rem", marginTop: 10, textAlign: "center" }}>
          {autoResume === "failed" && "Couldn't resume this device's saved work automatically."}
          {diag && <><br />Support code: {diag}</>}
        </p>
      )}
      </>
      )}
      </div>
    </div>
  );
}

const input = {
  display: "block",
  width: "100%",
  boxSizing: "border-box", // padding inside the 100%, so fields center like the buttons
  padding: "8px 10px",
  marginTop: 4,
  marginBottom: 12,
  borderRadius: 6,
  border: "1px solid var(--field-border)",
  background: "var(--field-bg)",
  color: "var(--field-fg)",
};

const button = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px",
  borderRadius: 6,
  border: "none",
  cursor: "pointer",
  fontSize: 16,
};